import React, { useState, useEffect } from 'react';
import { createWorkOrder, createCustomer, fetchUsers, fetchCustomer, patchCustomer } from './api';
import CarDiagram from './CarDiagram';
import TireDisposalToggle from './TireDisposalToggle';
import useDuplicateCustomerCheck from './customers/useDuplicateCustomerCheck';
import customerLabel from './customers/customerLabel';

const NewWorkOrder = ({ onWorkOrderCreated, onClose, initialData = null }) => {
  const { confirmNewCustomer, duplicatePrompt } = useDuplicateCustomerCheck();
  const [submissionError, setSubmissionError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The "Creating..." spinner hides while the duplicate-customer dialog waits on staff.
  const showCreating = isSubmitting && !duplicatePrompt;

  const [newWorkOrder, setNewWorkOrder] = useState({
    customerName: '',
    phone: '',
    email: '',
    vehicle: '',
    service_requested: '',
    number_of_wheels: '',
    tires: 'Yes',
    center_caps: 'Yes',
    tire_disposal: false,
    straightening_list: [],
    description: '',
    notes: '',
    price: '',
    date_promised: '',
    is_archived: false,
  });

  const [paymentMethod, setPaymentMethod] = useState('check'); // Default to check
  const [displayPrice, setDisplayPrice] = useState(''); // Price shown in input

  const [phoneValidity, setPhoneValidity] = useState({
    isValid: true,
    message: '',
  });

  // Initialize form with data from quote if provided
  useEffect(() => {
    if (initialData) {
      const workOrderData = {
        customerName: initialData.customer_name || initialData.customer_details?.name || '',
        phone: initialData.phone || '',
        email: initialData.customer_email || initialData.customer_details?.email || '',
        vehicle: initialData.vehicle || '',
        service_requested: initialData.service_requested || '',
        number_of_wheels: initialData.number_of_wheels || '',
        tires: initialData.tires || 'Yes',
        center_caps: initialData.center_caps || 'Yes',
        tire_disposal: !!initialData.tire_disposal,
        straightening_list: initialData.straightening_list_display || initialData.straightening_list || [],
        description: initialData.description || '',
        notes: initialData.notes || '',
        price: initialData.price || '',
        date_promised: initialData.date_promised || '',
        is_archived: false,
      };
      
      setNewWorkOrder(workOrderData);
      setDisplayPrice(initialData.price ? initialData.price.toString() : '');
      
      // Validate phone number if provided
      if (initialData.phone) {
        const phoneRegex = /^\d{10}$/;
        const isValid = phoneRegex.test(initialData.phone);
        setPhoneValidity({
          isValid: isValid,
          message: isValid ? '' : 'Phone number must be exactly 10 digits'
        });
      }
      
      // Set selected customer if customer ID exists
      if (initialData.customer) {
        setSelectedCustomer({
          id: initialData.customer,
          name: initialData.customer_name || initialData.customer_details?.name || ''
        });
        // Load the customer's saved email: it's what the email field starts from, and
        // what a staff edit is compared against.
        fetchCustomer(initialData.customer)
          .then((customer) => {
            setSelectedCustomer(customer);
            if (customer.email) {
              setNewWorkOrder(prev => ({ ...prev, email: customer.email }));
            }
          })
          .catch((error) => console.error('Error loading customer:', error));
      }
    }
  }, [initialData]);

  // Debounced customer search
  useEffect(() => {
    const searchCustomers = async () => {
      if (newWorkOrder.customerName.length < 2) {
        setCustomerSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetchUsers(newWorkOrder.customerName);
        setCustomerSearchResults(response.results || []);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [newWorkOrder.customerName]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setNewWorkOrder(prev => ({
      ...prev,
      customerName: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
    }));
    setShowDropdown(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewWorkOrder({
      ...newWorkOrder,
      [name]: type === 'checkbox' ? checked : value,
    });
    setHasUnsavedChanges(true);

    if (name === 'customerName') {
      setSelectedCustomer(null);
      setShowDropdown(true);
    }

    if (name === 'phone') {
      const pattern = /^[0-9]{10}$/;
      if (value.length > 0 && value.length < 10) {
        setPhoneValidity({
          isValid: false,
          message: `Please enter ${10 - value.length} more digits.`,
        });
      } else if (!pattern.test(value) && value.length >= 10) {
        setPhoneValidity({
          isValid: false,
          message: 'Invalid format. Please enter a 10-digit phone number.',
        });
      } else {
        setPhoneValidity({
          isValid: true,
          message: '',
        });
      }
    }

    if (name === 'price') {
      setDisplayPrice(value);
      const finalPrice = paymentMethod === 'credit_card' ? (parseFloat(value) || 0) * 1.03 : (parseFloat(value) || 0);
      setNewWorkOrder(prev => ({
        ...prev,
        price: finalPrice
      }));
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    const currentPrice = parseFloat(displayPrice) || 0;
    const finalPrice = method === 'credit_card' ? currentPrice * 1.03 : currentPrice;
    setNewWorkOrder(prev => ({
      ...prev,
      price: finalPrice
    }));
    setHasUnsavedChanges(true);
  };

  const handleWheelSelection = (selectedWheels) => {
    setNewWorkOrder(prev => ({
      ...prev,
      straightening_list: selectedWheels
    }));
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Example validation for empty fields
    if (!newWorkOrder.customerName) {
      setSubmissionError('Customer name is required.');
      return;
    }
    
    if (!newWorkOrder.phone || !phoneValidity.isValid) {
      setSubmissionError('A valid 10-digit phone number is required.');
      return;
    }

    const email = newWorkOrder.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubmissionError('Please enter a valid email address, or leave it blank.');
      return;
    }

    if (!newWorkOrder.vehicle.trim()) {
      setSubmissionError('Vehicle is required.');
      return;
    }

    // Validate number_of_wheels
    const wheelsValue = newWorkOrder.number_of_wheels.toString();
    if (!wheelsValue || wheelsValue === '0') {
      setSubmissionError('Number of wheels is required.');
      return;
    } else if (!/^\d+$/.test(wheelsValue)) {
      setSubmissionError('Number of wheels must be a numeric value.');
      return;
    }

    // Validate description
    if (!newWorkOrder.description.trim()) {
      setSubmissionError('Description is required.');
      return;
    }

    // Validate price
    const priceValue = newWorkOrder.price.toString();
    if (!priceValue || priceValue === '0') {
      setSubmissionError('Price is required.');
      return;
    } else if (!/^\d+(\.\d{1,2})?$/.test(priceValue)) {
      setSubmissionError('Price must be a numeric value.');
      return;
    }

    // Validate service requested
    if (!newWorkOrder.service_requested) {
      setSubmissionError('Service requested is required.');
      return;
    }

    // Validate date promised
    if (!newWorkOrder.date_promised.trim()) {
      setSubmissionError('Date promised is required.');
      return;
    }

    setSubmissionError('');
    setIsSubmitting(true);

    try {
      let customerId;
      
      if (selectedCustomer) {
        // Use existing customer. The email field started from their saved email, so a
        // different value means staff changed it — save it to the customer. A blank
        // field never clears an email already on file.
        customerId = selectedCustomer.id;
        const savedEmailKnown = 'email' in selectedCustomer; // not yet loaded => don't guess
        if (savedEmailKnown && email && email.toLowerCase() !== (selectedCustomer.email || '').toLowerCase()) {
          const updated = await patchCustomer(customerId, { email });
          setSelectedCustomer(updated);
        }
      } else {
        const customerData = {
          name: newWorkOrder.customerName,
          phone: newWorkOrder.phone,
          email,
        };
        const choice = await confirmNewCustomer(customerData);
        if (!choice) return; // back to the form
        let customer;
        if (choice.action === 'use') {
          customer = choice.customer;
          // Staff never saw this customer's saved email, so only fill it in if blank.
          if (email && !customer.email) {
            customer = await patchCustomer(customer.id, { email });
          }
        } else {
          customer = await createCustomer(customerData);
        }
        // Remember it, so retrying after a failed save doesn't ask (or create) again.
        setSelectedCustomer(customer);
        setNewWorkOrder(prev => ({ ...prev, customerName: customer.name }));
        customerId = customer.id;
      }

      if (customerId) {
        const workOrderData = {
          customer: customerId,
          phone: newWorkOrder.phone,
          vehicle: newWorkOrder.vehicle,
          service_requested: newWorkOrder.service_requested,
          number_of_wheels: parseInt(newWorkOrder.number_of_wheels),
          tires: newWorkOrder.tires || 'Yes',
          center_caps: newWorkOrder.center_caps || 'Yes',
          tire_disposal: !!newWorkOrder.tire_disposal,
          straightening_list: newWorkOrder.straightening_list || [],
          description: newWorkOrder.description,
          notes: newWorkOrder.notes || '',
          price: parseFloat(newWorkOrder.price),
          payment_method: paymentMethod,
          date_promised: newWorkOrder.date_promised,
          is_archived: false,
          status: newWorkOrder.status || 'In Process'
        };
        
        const createdWorkOrder = await createWorkOrder(workOrderData);

        if (createdWorkOrder) {
          onWorkOrderCreated(createdWorkOrder);
          setNewWorkOrder({
            customerName: '',
            phone: '',
            email: '',
            vehicle: '',
            service_requested: '',
            number_of_wheels: '',
            tires: 'Yes',
            center_caps: 'Yes',
            tire_disposal: false,
            straightening_list: [],
            description: '',
            notes: '',
            price: '',
            date_promised: '',
            is_archived: false,
          });
          setSelectedCustomer(null);
          setPaymentMethod('check');
          setDisplayPrice('');
        }
      }
    } catch (error) {
      console.error('Error creating work order:', error);
      setSubmissionError(`Failed to create work order: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
  };

  return (
    <form onSubmit={handleSubmit} className='workorder'>
      <div className="workorder-header-container">
        <h2 className='workorderheader'>New Work Order</h2>
        <button type="button" className="close-button" onClick={handleClose}>×</button>
      </div>
      <div className='workorderform'>
        <div className="workordertop">
          <div className='workordercontact'>
            <div className='workordercontactcolumn'>
              {/* Customer Name with Dropdown */}
              <div className="customer-search-container">
                <label className='workorderlabelcontact'>
                  Name:
                  <input
                    className='workorderinputscontact'
                    type="text"
                    name="customerName"
                    value={newWorkOrder.customerName}
                    onChange={handleChange}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    placeholder="Search or enter new customer"
                  />
                </label>
                {showDropdown && newWorkOrder.customerName.length >= 2 && (
                  <div className="customer-dropdown">
                    {isSearching ? (
                      <div className="customer-dropdown-item">Searching...</div>
                    ) : customerSearchResults.length > 0 ? (
                      customerSearchResults.map((customer) => (
                        <div
                          key={customer.id}
                          className="customer-dropdown-item"
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customerLabel(customer)}
                        </div>
                      ))
                    ) : (
                      <div className="customer-dropdown-item">No customers found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Phone */}
              <label className='workorderlabelcontact'>
                Phone:
                <input
                  className={`workorderinputscontact ${!phoneValidity.isValid ? 'invalid' : ''}`}
                  type="tel"
                  name="phone"
                  value={newWorkOrder.phone}
                  onChange={handleChange}
                  placeholder="1234567890"
                />
                {!phoneValidity.isValid && <span className="workorderphoneErrorStyle">{phoneValidity.message}</span>}
              </label>
            </div>

            <div className='workordercontactcolumn'>
              {/* Vehicle */}
              <label className='workorderlabelcontact'>
                Vehicle:
                <input
                  className='workorderinputscontact'
                  type="text"
                  name="vehicle"
                  value={newWorkOrder.vehicle}
                  onChange={handleChange}
                />
              </label>

              {/* Email — saved on the customer, not the work order */}
              <label className='workorderlabelcontact'>
                Email:
                <input
                  className='workorderinputscontact'
                  type="email"
                  name="email"
                  value={newWorkOrder.email}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </label>
            </div>
          </div>

          <div className='workorderimportantinfo'>
            {/* Price */}
            <label className='workorderlabelimportantinfo'>
              Price:
              <div className="price-payment-container">
                <input
                  className='workorderinputsprice'
                  type="number"
                  step="0.01"
                  name="price"
                  value={displayPrice}
                  onChange={handleChange}
                />
                <div className="payment-method-slider">
                  <button
                    type="button"
                    className={`payment-method-btn ${paymentMethod === 'check' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('check')}
                    title="Check"
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className={`payment-method-btn credit-card ${paymentMethod === 'credit_card' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('credit_card')}
                    title="Credit Card (+3%)"
                  >
                    💳
                  </button>
                </div>
              </div>
            </label>

            {/* Date Promised */}
            <label className='workorderlabelimportantinfo'>
              Date Promised:
              <input
                className='workorderinputsprice'
                type="date"
                name="date_promised"
                value={newWorkOrder.date_promised}
                onChange={handleChange}
              />
            </label>

            {/* Status */}
            <label className='workorderlabelimportantinfo'>
              Status:
              <select
                className='workorderinputsprice'
                name="status"
                value={newWorkOrder.status}
                onChange={handleChange}
              >
                <option value="In Process">In Process</option>
                <option value="Called">Called</option>
                <option value="Picked Up">Picked Up</option>
              </select>
            </label>
          </div>
        </div>

        <div className='workorderdetails'>
          <div className="workorderdetailssmall">
            {/* Service Requested */}
            <label className='workorderlabeldetails'>
              Service Requested:
              <select
                className='workorderinputsdetails'
                name="service_requested"
                value={newWorkOrder.service_requested}
                onChange={handleChange}
              >
                <option value="" disabled>Select Service</option>
                <option value="Restoration">Restoration</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Straightening">Straightening</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>

            {/* Number of Wheels/Parts */}
            <label className='workorderlabeldetails'>
              Number of Wheels/Parts:
              <input
                className='workorderinputsdetails'
                type="number"
                name="number_of_wheels"
                value={newWorkOrder.number_of_wheels}
                onChange={handleChange}
              />
            </label>

            {/* Tires, with Tire Disposal below it (beside it on mobile) */}
            <div className='workorder-tires-group'>
              <label className='workorderlabeldetails'>
                Tires:
                <select
                  className='workorderinputsdetails'
                  name="tires"
                  value={newWorkOrder.tires}
                  onChange={handleChange}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="New">New</option>
                </select>
              </label>
              <label className='workorderlabeldetails'>
                Tire Disposal:
                <TireDisposalToggle
                  boxClassName='workorderinputsdetails'
                  checked={newWorkOrder.tire_disposal}
                  onChange={(checked) => {
                    setNewWorkOrder(prev => ({ ...prev, tire_disposal: checked }));
                    setHasUnsavedChanges(true);
                  }}
                />
              </label>
            </div>

            {/* Center Caps */}
            <label className='workorderlabeldetails'>
              Center Caps:
              <select
                className='workorderinputsdetails'
                name="center_caps"
                value={newWorkOrder.center_caps}
                onChange={handleChange}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            {/* Straightening */}
            <label className='workorderlabeldetails'>
              Straightening:
              <CarDiagram
                selectedWheels={newWorkOrder.straightening_list}
                onWheelSelection={handleWheelSelection}
              />
            </label>
          </div>

          <div className='workorderdetailslarge'>
            {/* Description */}
            <label className='workorderlabeldetailslarge'>
              Description:
              <textarea
                className='workorderinputsdetailslarge'
                name="description"
                value={newWorkOrder.description}
                onChange={handleChange}
              />
            </label>

            {/* Notes */}
            <label className='workorderlabeldetailslarge'>
              Notes:
              <textarea
                className='workorderinputsdetailslarge'
                name="notes"
                value={newWorkOrder.notes}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>
      </div>

      <div className='workordersavebuttonbanner'>
        {/* Desktop buttons */}
        <div className="desktop-buttons">
          <div>{submissionError && (
            <div className="workordersubmissionErrorStyle">
              {submissionError}
            </div>
          )}</div>
          <button
            className={`workordersavebutton ${showCreating ? 'workordersavebutton-loading' : ''}`}
            type="submit"
            disabled={isSubmitting}
          >
            {showCreating ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </div>

        {/* Mobile buttons */}
        <div className="mobile-buttons">
          <div>{submissionError && (
            <div className="workordersubmissionErrorStyle">
              {submissionError}
            </div>
          )}</div>
          <button
            className={`mobile-save-button ${showCreating ? 'mobile-save-button-loading' : ''}`}
            type="submit"
            disabled={isSubmitting}
          >
            {showCreating ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </div>
      </div>
      {showUnsavedWarning && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <div className="unsaved-changes-buttons">
              <button onClick={handleConfirmClose}>Leave</button>
              <button onClick={handleCancelClose}>Stay</button>
            </div>
          </div>
        </div>
      )}
      {duplicatePrompt}
    </form>
  );
};

export default NewWorkOrder;
